import { DateTime } from '@ksyos/date-time';
import { Card, Form, FormButtonRow, FormGroup, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEmployment } from '@common/interfaces';
import type { IOrganization } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, PractitionerRole, ValidationErrorCode } from '@common/enums/';
import { BankAccountEntity } from '@common/enums/bankAccountEntity';
import { getNestedTranslation } from '@common/translate/translate';
import { organizationFinancialSettingsSchema } from '@common/validation/organization';
import { InputTriState } from '@frontend/components/elements/common';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { DatePickerFormGroupField } from '@frontend/components/fields/DatePickerField';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { InputTextField } from '@frontend/components/userDataManagement/InputText';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { hasPractitionerRole } from '@frontend/utils/hasPractitionerRole';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import { BankAccountDetails } from '../../bankAccountDetails/BankAccountDetails';
import { BankAccountDetailsLogs } from '../../bankAccountDetails/BankAccountDetailsLogs';

type OrganizationFinancialSettingsProps = {
    isReadOnly: boolean;
    organizationId: string;
};

export function OrganizationFinancialSettings({ isReadOnly, organizationId }: OrganizationFinancialSettingsProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [organization, setOrganization] = useState<IOrganization>();
    const [originalOrganization, setOriginalOrganization] = useState<IOrganization>();
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const isOrganizationPayoutManager = hasPractitionerRole(
        useSelector((state: IReduxState) => state.authentication).employmentPractitionerRoles,
        PractitionerRole.organizationPayoutManager
    );
    const canEditFinancialData = hasOneAuthorization(ManagerAuthorization.organizationFinanceData);

    if (!isOrganizationPayoutManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    useEffect(() => {
        loadOrganization(organizationId);
    }, [organizationId]);

    if (!organization) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                errors: errors,
                item: organization,
                onChange: (key, value) => handleChange(key, value),
                onDoValidate: (key) => validateOne(key),
                placeholderGroupName: 'financialSettings',
            }}
        >
            <Card>
                <Card.Transition type={isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        !isReadOnly && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translate.common.financialData}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {!isOrganizationPayoutManager &&
                            renderTextInput({
                                fieldKey: 'relationNumber',
                                label: 'organization.relationNumber',
                                readOnly: !hasOneAuthorization(ManagerAuthorization.organizationRelationNumberEdit),
                                value: organization.relationNumber,
                            })}
                        {renderTextInput({
                            fieldKey: 'emailAddressPayouts',
                            label: 'organization.emailAddressPayouts',
                            readOnly: !hasOneAuthorization([
                                ManagerAuthorization.organizationAddEdit,
                                ManagerAuthorization.organizationRelationNumberEdit,
                            ]),
                            value: organization.emailAddressPayouts,
                        })}
                        {!isOrganizationPayoutManager &&
                            !isEditing &&
                            renderTextInput({
                                fieldKey: 'suppressEmployeePayoutOverview',
                                label: 'finance.suppressEmployeePayoutOverview',
                                readOnly: true,
                                tooltipText: translate.finance.suppressEmployeePayoutOverviewTooltip,
                                value: organization.suppressEmployeePayoutOverview ? 'Ja' : 'Nee',
                            })}

                        {isEditing && (
                            <FormGroup
                                label={translate.finance.suppressEmployeePayoutOverview}
                                tooltipText={translate.finance.suppressEmployeePayoutOverviewTooltip}
                            >
                                <InputTriState
                                    error={getValidationErrorTranslation(errors.get('suppressEmployeePayoutOverview'))}
                                    fieldKey="suppressEmployeePayoutOverview"
                                    name="suppressEmployeePayoutOverview"
                                    notNullable
                                    onChange={async (value) =>
                                        handleChange('suppressEmployeePayoutOverview', Boolean(value))
                                    }
                                    value={organization.suppressEmployeePayoutOverview}
                                />
                            </FormGroup>
                        )}

                        {!isOrganizationPayoutManager &&
                            renderDateInput({
                                fieldKey: 'organizationLevelPayoutsStartDate',
                                label: 'organization.organizationLevelPayoutsStartDate',
                                readOnly: !canEditFinancialData,
                                tooltipText: translate.organization.organizationLevelPayoutsStartDateTooltip,
                                value: organization.organizationLevelPayoutsStartDate,
                            })}

                        {(canEditFinancialData || isOrganizationPayoutManager) && (
                            <>
                                <BankAccountDetails
                                    bankAccountEntity={BankAccountEntity.organization}
                                    canProcessBankAccountChanges={isOrganizationPayoutManager ? false : true}
                                    canRequestChange={isOrganizationPayoutManager ? true : false}
                                    entityId={organizationId}
                                />

                                {canEditFinancialData && !isEditing && (
                                    <BankAccountDetailsLogs
                                        bankAccountEntity={BankAccountEntity.organization}
                                        entityId={organizationId}
                                    />
                                )}
                            </>
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving}
                                    onSave={handleSubmit}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderTextInput({
        fieldKey,
        label,
        readOnly,
        value,
        tooltipText,
    }: {
        fieldKey: string;
        label: string;
        readOnly: boolean;
        tooltipText?: string;
        value?: string | null;
    }) {
        if (isEditing && !readOnly) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    label={label}
                    nullable
                    tooltipText={tooltipText}
                    value={value}
                />
            );
        }

        const formParagraphLabel = getNestedTranslation(label);

        return (
            <FormParagraphFormGroup
                label={formParagraphLabel}
                tooltipText={tooltipText}
            >
                {value || '-'}
            </FormParagraphFormGroup>
        );
    }

    function renderDateInput({
        fieldKey,
        label,
        readOnly,
        tooltipText,
        value,
    }: {
        fieldKey: keyof IOrganization;
        label: string;
        readOnly?: boolean;
        tooltipText?: string;
        value?: string | null;
    }) {
        const labelTranslation = getNestedTranslation(label);

        return isEditing && !readOnly ? (
            <DatePickerFormGroupField
                fieldKey={fieldKey}
                label={labelTranslation}
                layout="horizontal"
                onChange={async (date) =>
                    handleChange(fieldKey, date ? (DateTime.from(date).format('YYYY-MM-DD') as any) : null)
                }
                onDoValidate={() => ({})}
                tooltipText={tooltipText}
                value={value ? DateTime.from(value).toDate() : null}
            />
        ) : (
            <FormParagraphFormGroup
                label={labelTranslation}
                tooltipText={tooltipText}
            >
                <NullableFormattedDate value={value} />
            </FormParagraphFormGroup>
        );
    }

    async function loadOrganization(id: string) {
        const url = `/api/organizations/${id}?includeBankAccountDetails=true`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        setOriginalOrganization(result.json);
        setOrganization(result.json);
    }

    function handleChange<K extends keyof IOrganization>(name: K, value: IOrganization[K]) {
        setOrganization({ ...organization, [name]: value } as IOrganization);
    }

    function handleCancel() {
        setOrganization(originalOrganization);
        setErrors(emptyValidationErrors);
        setIsEditing(false);
    }

    function validateOne(name: keyof IEmployment) {
        setErrors(updateValidationErrorsForOne(organizationFinancialSettingsSchema, organization, errors, name));
    }

    async function handleSubmit() {
        if (organization) {
            setIsSaving(true);

            const { errors: validationErrors, item } = validate(organizationFinancialSettingsSchema, organization);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                const url = `/api/organizations/${organization.id}/financialSettings`;
                const result = await abortableFetch.fetchJson(url, 'POST', item, [
                    ValidationErrorCode.RELATION_NUMBER_EXISTS,
                ]);

                if (result?.errorCode === ValidationErrorCode.RELATION_NUMBER_EXISTS) {
                    setErrors(emptyValidationErrors.set('relationNumber', ValidationErrorCode.RELATION_NUMBER_EXISTS));
                } else {
                    setErrors(emptyValidationErrors);
                    setOriginalOrganization(organization);
                    setIsEditing(false);
                }
            }
            setIsSaving(false);
        }
    }
}
